import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';

const TABLET_MIN_DIMENSION = 768; // This is the minimum size of iPad tablets

interface UISettingsType {
  showHUD: boolean;
  allowAnonymous: boolean;
  isMobileDevice: boolean;
  isNativeApp: boolean;
  isTablet: boolean;
}

const UIContext = createContext<UISettingsType | undefined>(undefined);

interface UISettingsProps {
  children: ReactNode;
}

export const UISettingsProvider: React.FC<UISettingsProps> = ({ children }) => {
  const showHUD = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return !urlParams.has('noHUD') || urlParams.get('noHUD') === 'false';
  }, []); // Remember it upon first enter and don't update on navigation

  const allowAnonymous = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return (
      urlParams.has('allowAnonymous') &&
      urlParams.get('allowAnonymous') !== 'false'
    );
  }, []); // Remember it upon first enter and don't update on navigation

  const isMobileDevice = useMemo(() => {
    return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  }, []);

  const isNativeApp = useMemo(() => {
    return Capacitor.isNativePlatform();
  }, []);

  // This assumes that the device is a tablet if the screen is at least 768px in the smallest dimension
  // It also assumes that the web app is opened in fullscreen mode, which is standard for mobile devices
  const isTablet = useMemo(() => {
    return (
      Math.min(window.innerWidth, window.innerHeight) >= TABLET_MIN_DIMENSION
    );
  }, []);

  return (
    <UIContext.Provider
      value={{
        showHUD,
        allowAnonymous,
        isMobileDevice,
        isNativeApp,
        isTablet,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export const useUISettings = () => {
  const context = useContext(UIContext);

  if (context === undefined) {
    throw new Error('useUI must be used within an UISettingsProvider');
  }
  return context;
};
